import { css, Rule } from 'glamor'
import { omit } from 'lodash-es'
import * as PropTypes from 'prop-types'
import * as React from 'react'
import { compose, mapProps, withState } from 'recompose'

import { IInputProps } from '../../types'
import { useTheme } from '../../theme'

import getStyles from './styles'

const variants = ['checkbox', 'toggle']

export interface ICheckboxProps {
  block?: boolean
  children?: React.ReactNode
  flush?: boolean
  card?: boolean
  checked?: boolean
  defaultChecked?: boolean
  disabled?: boolean
  readOnly?: boolean
  variant?: 'checkbox' | 'toggle'
  fillStyle?: 'check' | 'dash'
  align?: 'left' | 'right'
  deselect?: boolean
  required?: boolean
  style?: Rule
  input?: IInputProps<React.ChangeEvent<HTMLInputElement>>
  onClick?: (e: React.SyntheticEvent) => void
}

export function Checkbox({
  block,
  card,
  checked,
  children,
  defaultChecked,
  disabled,
  flush,
  variant,
  align = 'left',
  deselect,
  style,
  input,
  readOnly,
  fillStyle = 'check',
  ...additionalProps
}: ICheckboxProps) {
  const styles = getStyles(useTheme())

  const inputProps: IInputProps<React.ChangeEvent<HTMLInputElement>> = omit(
    {
      ...css(styles.input.base, disabled && styles.input.disabled, readOnly && styles.input.readOnly),
      type: 'checkbox',
      checked,
      defaultChecked,
      disabled,

      'aria-checked': checked,
      ...input,
      ...additionalProps,
    },
    ['meta', 'hasErrored', 'onClick']
  )

  const backgroundInputElement = <input {...inputProps} readOnly={readOnly || !inputProps.onChange} />
  const onWrapperClick = (e: React.SyntheticEvent) => {
    if (additionalProps.onClick) {
      additionalProps.onClick(e)
    }
  }
  return (
    // keyboard navigation is accomplished with the input element focus
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
    <div
      onClick={onWrapperClick}
      {...css(
        styles.outerWrapper.base,
        card && styles.outerWrapper.card,
        card && checked && styles.outerWrapper.cardChecked,
        !card && !!children && styles.outerWrapper.hasChildren,
        flush && styles.outerWrapper.flush
      )}
    >
      <span
        {...css(
          styles.focusWrapper.base,
          !card && styles.focusWrapper.focused,
          card && styles.focusWrapper.card,
          align === 'right' && styles.focusWrapper.right
        )}
      >
        <span
          {...css(
            styles.wrapper.base,
            !disabled && !readOnly && styles.wrapper.normal,
            variant === 'toggle' && styles.wrapper.toggle,
            !!children && (align === 'left' ? styles.wrapper.hasChildrenLeft : styles.wrapper.hasChildrenRight),
            block && styles.wrapper.block,
            style
          )}
        >
          {variant === 'checkbox' && (
            <CheckboxIcon
              checked={checked || defaultChecked}
              deselect={deselect}
              disabled={disabled}
              fillStyle={fillStyle}
              readOnly={readOnly}
            />
          )}
          {variant === 'toggle' && (
            <div
              {...css(
                ...[
                  styles.toggle.base,
                  (checked || defaultChecked) && styles.toggle.on,
                  disabled && styles.toggle.disabled,
                  disabled && checked && styles.toggle.disabledOn,
                  readOnly && styles.toggle.readOnly,
                  readOnly && checked && styles.toggle.readOnlyOn,
                ]
              )}
            >
              <span
                {...css(
                  styles.toggleIndicator.base,
                  checked || defaultChecked ? styles.toggleIndicator.on : styles.toggleIndicator.off
                )}
              />
            </div>
          )}

          {!card && !children && backgroundInputElement}
        </span>

        {(card || children) && backgroundInputElement}

        {children && (
          <span
            {...css(
              styles.label.base,
              disabled && styles.label.disabled,
              readOnly && styles.label.readOnly,
              flush && styles.label.flush,
              align === 'left' ? styles.label.left : styles.label.right
            )}
          >
            {children}
          </span>
        )}
      </span>
    </div>
  )
}

Checkbox.propTypes = {
  block: PropTypes.bool,
  children: PropTypes.any,
  flush: PropTypes.bool,
  card: PropTypes.bool,
  checked: PropTypes.bool,
  defaultChecked: PropTypes.bool,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  variant: PropTypes.oneOf(variants),
  deselect: PropTypes.bool,
  style: PropTypes.object,
  input: PropTypes.object,
}

Checkbox.defaultProps = {
  variant: 'checkbox',
}

Checkbox.displayName = 'Checkbox'

interface ICheckboxIconProps {
  checked?: boolean
  disabled?: boolean
  readOnly?: boolean
  deselect?: boolean
  hovered?: boolean
  fillStyle?: 'check' | 'dash'
  setHovered?: (hovered: boolean) => void
  onMouseOver?: (e: React.SyntheticEvent) => void
  onMouseOut?: (e: React.SyntheticEvent) => void
}

const enhanceCheckboxIcon = compose<ICheckboxIconProps, ICheckboxIconProps>(
  withState('hovered', 'setHovered', false),
  mapProps(({ setHovered, onMouseOver, onMouseOut, ...additional }: ICheckboxIconProps) => ({
    onMouseOver: (event: React.SyntheticEvent) => {
      if (setHovered) setHovered(true)
      if (onMouseOver) onMouseOver(event)
    },
    onMouseOut: (event: React.SyntheticEvent) => {
      if (setHovered) setHovered(false)
      if (onMouseOut) onMouseOut(event)
    },
    ...additional,
  }))
)

const CheckboxIcon = enhanceCheckboxIcon(
  ({
    checked,
    disabled,
    readOnly,
    deselect,
    hovered,
    fillStyle = 'check',
    onMouseOver,
    onMouseOut,
  }: ICheckboxIconProps) => {
    const styles = getStyles(useTheme())

    const containerProps = {
      onMouseOver,
      onMouseOut,
    }

    const pathProps = {
      ...css(
        !disabled && !readOnly && !hovered && styles.checkboxFillPath.normal,
        !disabled && !readOnly && hovered && styles.checkboxFillPath.hovered,
        disabled && styles.checkboxFillPath.disabled,
        readOnly && styles.checkboxFillPath.readOnly
      ),
    }

    if (!checked) {
      return (
        <svg {...containerProps} height="20" viewBox="0 0 20 20" width="20" xmlns="http://www.w3.org/2000/svg">
          <g fill="none" fillRule="evenodd">
            <path d="M0 0h20v20H0z" />
            <path
              d="M18 0H2C.897 0 0 .897 0 2v16c0 1.103.897 2 2 2h16c1.104 0 2-.897 2-2V2c0-1.103-.896-2-2-2zM3.333 1.667c-.919 0-1.666.747-1.666 1.666v13.334c0 .919.747 1.666 1.666 1.666h13.334c.92 0 1.666-.747 1.666-1.666V3.333c0-.919-.746-1.666-1.666-1.666H3.333z"
              fill={
                disabled || readOnly
                  ? 'rgba(153, 153, 153, 0.125)'
                  : hovered
                  ? 'rgba(153, 153, 153, 0.4)'
                  : 'rgba(182, 198, 214, 0.5)'
              }
            />
          </g>
        </svg>
      )
    }

    if (checked && !deselect) {
      return fillStyle === 'dash' ? (
        <svg height="20" style={{ display: 'block' }} viewBox="0 0 20 20" width="20" xmlns="http://www.w3.org/2000/svg">
          <g fill="none" fillRule="evenodd">
            <path d="M0 0h20v20H0z" />
            <path
              d="M18 0H2C.897 0 0 .897 0 2v16c0 1.103.897 2 2 2h16c1.104 0 2-.897 2-2V2c0-1.103-.896-2-2-2z"
              {...pathProps}
            />
            <rect fill="#FFF" height="2" rx="1" width="8" x="6" y="9" />
          </g>
        </svg>
      ) : (
        <svg {...containerProps} height="20" viewBox="0 0 20 20" width="20" xmlns="http://www.w3.org/2000/svg">
          <g fill="none" fillRule="evenodd">
            <path d="M0 0h20v20H0z" />
            <path
              d="M18 0H2C.897 0 0 .897 0 2v16c0 1.103.897 2 2 2h16c1.104 0 2-.897 2-2V2c0-1.103-.896-2-2-2z"
              {...pathProps}
            />
            <path
              d="M9.671 12.75a.832.832 0 0 1-1.19-.002L6.248 10.47a.87.87 0 0 1-.005-1.213l.122-.125a.831.831 0 0 1 1.194.01l1.517 1.548 3.363-3.433a.832.832 0 0 1 1.194-.01l.122.124a.871.871 0 0 1-.002 1.211L9.67 12.75z"
              fill="#FFF"
            />
          </g>
        </svg>
      )
    }

    return (
      <svg {...containerProps} height="20" viewBox="0 0 20 20" width="20" xmlns="http://www.w3.org/2000/svg">
        <g fill="none" fillRule="evenodd">
          <path d="M0 0h20v20H0z" />
          <path
            d="M18 0H2C.897 0 0 .897 0 2v16c0 1.103.897 2 2 2h16c1.104 0 2-.897 2-2V2c0-1.103-.896-2-2-2z"
            {...pathProps}
          />
          <rect fill="#FFF" height="2" rx="1" width="8" x="6" y="9" />
        </g>
      </svg>
    )
  }
)
